import { useCallback } from 'react'
import { useReactFlow } from 'reactflow'
import { useCanvasEdges } from '../../CanvasEdgesContext'
import { buildAssetDownloadFilename } from '../../../../utils/buildDownloadFilename'
import type { ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import type { HistoryEntry } from './types'
import { useNodeActions } from './useNodeActions'

interface UseVariantImageDownloadOptions {
  nodeId: string
  history: HistoryEntry[]
}

/**
 * Canonical per-variant image download handler. The clicked history index is
 * intentionally used for filename derivation instead of the node's currently
 * picked index: downloading a hover tile must name that tile, without selecting
 * it as a side effect.
 */
export function useVariantImageDownload({
  nodeId,
  history,
}: UseVariantImageDownloadOptions): (assetPath: string, historyIndex: number) => Promise<void> {
  const { getNodes } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const edges = useCanvasEdges()
  const { handleDownload } = useNodeActions({ nodeId })

  return useCallback(
    async (assetPath: string, historyIndex: number) => {
      const { filename } = buildAssetDownloadFilename({
        canvasNodes: getNodes(),
        canvasEdges: edges,
        nodeId,
        assetPath,
        mediaType: 'image',
        variantHistory: history,
        pickedIndex: historyIndex,
        trigger: 'variants-panel',
      })
      await handleDownload(assetPath, filename, 'variants-panel', historyIndex)
    },
    [edges, getNodes, handleDownload, history, nodeId],
  )
}
